import { Option } from "../components/shared/selectInput/types/selectInputProps";
import { notifyWarn } from "../components/snackbar/snackbar";
import { ObjectCategories } from "../containers/centralView/administrationView/AdministrationPermissionExtendedView/types/administrationsPermissionExtendedViewProps";
import { ObjectState } from "../containers/centralView/taskListView/types/tasklListViewProps";
import { Roles } from "../contexts/authContext";
import { ExtendedUserPermissions, FunctionTypes, UseCaseDefinitions } from "../contexts/permissionsContext";
import { ObjectType, PatternType, toPattern } from "../contexts/types/card";

export const categories: Option[] = [
    { value: ObjectCategories.Landslide, name: "Osuwisko"},
    { value: ObjectCategories.EndangeredArea, name: "Teren zagrożony"},
    { value: ObjectCategories.Protocol, name: "Protokół obserwacji"}
]

export const adminDecisionIconVisible = (state: ObjectState, type: ObjectType) => {
    return [ObjectState.AnalysisDecision, ObjectState.AnalysisError].includes(state) && [ObjectType.KRO, ObjectType.KRTZ].includes(type);
}

export const disableReportIcon = (state: ObjectState) => {
    return [ObjectState.AnalysisDecision, ObjectState.AnalysisError, ObjectState.AnalysisInProgress, ObjectState.AnalysisSuccess, ObjectState.Uploaded].includes(state);
}

export const prepareUserOptions = (data: any) => {
    var usersSelect: Option[] = data.map((item: any) => {
        return {
            value: item.id,
            name: `${item.nazwA_PELNA} (${item.nazwa})`
        };
    });

    return usersSelect.sort((a: any, b: any) => a.name.localeCompare(b.name, 'pl'));
}

export const powiatsToOptions = (obj: any[], wojDict: any[]) => {
    var options: Option[] = [];
    obj.forEach(item => {
        var woj = wojDict.find((elem: any) => elem.id === item.wojewodztwO_ID);
        options.push({ value: item.id, name: `${item.nazwa}, woj. ${woj.nazwa}` });
    });
    return options;
}

export const gminyToOptions = (obj: any[], powiatDict: any[]) => {
    var options: Option[] = [];
    obj.forEach(item => {
        var powiat = powiatDict.find((elem: any) => elem.id === item.powiaT_ID);
        var gminaType = "";
        switch (item.teryt.slice(-1)) {
            case "1":
                gminaType = " g. miejska,";
                break;
            case "2":
                gminaType = " g. wiejska,";
                break;
            case "3":
                gminaType = " g. miejsko-wiejska,";
        }
        options.push({ value: item.id, name: `${item.nazwa},${gminaType} p. ${powiat.nazwa}` });
    });
    return options;
}

export const convertUserObjectCategoriesToOptions = (userObjectCategories: any[]) => {
    var selected: Option[] = [];
    userObjectCategories.forEach(item => {
        switch(item.rodzaJ_OBIEKTU_DOM) {
            case "osuwisko":
                selected.push(categories.find(elem => elem.value === ObjectCategories.Landslide)!);
                break;
            case "tz":
                selected.push(categories.find(elem => elem.value === ObjectCategories.EndangeredArea)!);
                break;
            case "po":
                selected.push(categories.find(elem => elem.value === ObjectCategories.Protocol)!);
        }
    })
    return selected;
}

export const convertUserObjectCategoriesToEnum = (userObjectCategories: any[]) => {
    var selected: ObjectCategories[] = [];
    userObjectCategories.forEach(item => {
        switch(item.rodzaJ_OBIEKTU_DOM) {
            case "osuwisko":
                selected.push(ObjectCategories.Landslide);
                break;
            case "tz":
                selected.push(ObjectCategories.EndangeredArea);
                break;
            case "po":
                selected.push(ObjectCategories.Protocol);
        }
    })
    return selected;
}

export const convertUserObjectCategoryOptionsToStrings = (categorySelectedOptions: any[]) => {
    var categories: string[] = [];
    
    categorySelectedOptions.forEach(item => {
        switch(item.value) {
            case ObjectCategories.Landslide:
                categories.push("osuwisko");
                break;
            case ObjectCategories.EndangeredArea:
                categories.push("tz");
                break;
            case ObjectCategories.Protocol:
                categories.push("po");
        }
    })
    return categories;
}

export const convertTerytOptionsToStrings = (powiatTerytOptions: any[], gminaTerytOptions: any[], powiatDict: any[], gminaDict: any[]) => {
    var assignedTeryts: string[] = [];
    powiatTerytOptions.forEach(item => assignedTeryts.push(powiatDict.find((elem: any) => elem.id === item.value).teryt));
    gminaTerytOptions.forEach(item => assignedTeryts.push(gminaDict.find((elem: any) => elem.id === item.value).teryt));
    return assignedTeryts;
}

export const canBeVerified = (functionsPermissions: FunctionTypes, objectStatus: string, isWaitingRoom?: boolean) => {
    
    // Check function permission
    if (!functionsPermissions.verifyKR.active) return false;

    // Check if verification can be done on downloaded records
    if (isWaitingRoom && objectStatus === ObjectState.DownloadedForVerification) return false;

    // Check object status permissions
    const verifiable = [ObjectState.ToBeVerified, ObjectState.WaitingRoomEditionVerification, ObjectState.DownloadedForVerification];
    return verifiable.includes(objectStatus as ObjectState);
}

export const checkAssigment = (user: string, assignedUser: string, userRoles: string[]) => {
    const isAdmin = userRoles.includes(Roles.PwaAdmin);
    return (assignedUser !== null && assignedUser !== "" && user === assignedUser) || isAdmin;
}

export const canBeEdited = (functionsPermissions: FunctionTypes, waitingroom: any, extendedUserPermissions: ExtendedUserPermissions, isWaitingRoom?: boolean, isCardEditing?: boolean) => {
    const objType = waitingroom.RODZAJ_OBIEKTU;
    const objectStatus = waitingroom.STAN_OBIEKTU_DOM;
    const teryts = waitingroom.TERYT as string;

    if (objType === ObjectType.KRO || objType === ObjectType.KRTZ) {
        
        // Check function permission
        if (!functionsPermissions.EditKR.active) return false;

        // Check if edit can be made on downloaded records
        if (isWaitingRoom && objectStatus === ObjectState.DownloadedForEdition) return false;

        var canEditKR = false;

        // Check object status permissions
        const editable = [ObjectState.Assigned, ObjectState.ToBeCorrected, ObjectState.Complete, ObjectState.WaitingRoomEdition, ObjectState.DownloadedForEdition];
        canEditKR = editable.includes(objectStatus as ObjectState);

        // Check extended user permissions
        if (canEditKR) {
            if (extendedUserPermissions.userObjectCategories?.length! > 0) {
                if (objType === ObjectType.KRO) canEditKR = extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.Landslide)!;
                if (objType === ObjectType.KRTZ) canEditKR = extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.EndangeredArea)!;
                
                if (!canEditKR && isCardEditing) {
                    notifyWarn({ message: "Nie posiadasz uprawnień do edycji tego rodzaju obiektu." });
                    return false;
                }
            }
            if (teryts && teryts !== null && (extendedUserPermissions.userGminy?.length! > 0 || extendedUserPermissions.userPowiaty?.length! > 0)) {
                var terytListKR = teryts.split(', ');
                console.log("terytListKR", terytListKR);
                canEditKR = terytListKR.some(item => 
                    {
                        return extendedUserPermissions.userGminy?.includes(item) || extendedUserPermissions.userPowiaty?.includes(item.substring(0, 4))
                    });
                if (!canEditKR && isCardEditing){
                    notifyWarn({ message: "Nie posiadasz uprawnień przestrzennych do otworzenia karty." });
                }
            }
        }
        return canEditKR;
    }
    else if (objType === ObjectType.POO || objType === ObjectType.POTZ) {
        
        // Check function permission
        if (!functionsPermissions.EditPO.active) return false;

        // Check if edit can be made on downloaded records
        if (isWaitingRoom && objectStatus === ObjectState.DownloadedForEdition) return false;

        var canEditPO = false;

        // Check object status permissions
        const editable = [ObjectState.Assigned, ObjectState.WaitingRoomEdition, ObjectState.DownloadedForEdition];
        canEditPO = editable.includes(objectStatus as ObjectState);

        // Check extended user permissions
        if (canEditPO) {
            if (extendedUserPermissions.userObjectCategories?.length! > 0) {
                canEditPO = extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.Protocol)! &&
                (objType === ObjectType.POO ? extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.Landslide)! : 
                extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.EndangeredArea)!);
                if (!canEditPO) return false; 
            }   
            if (teryts && teryts !== null && (extendedUserPermissions.userGminy?.length! > 0 || extendedUserPermissions.userPowiaty?.length! > 0)) {
                var terytListPO = teryts.split(', ');
                canEditPO = terytListPO.some(item => 
                    extendedUserPermissions.userGminy?.includes(item) || extendedUserPermissions.userPowiaty?.includes(item.substring(0, 4)));
            }          
        }

        return canEditPO;
    }     
}

export const canBeCollectivelyEdited = (cardPattern: string, objectStatus: string, objType: ObjectType, extendedUserPermissions: ExtendedUserPermissions,
    teryts: string, hasGeometry: boolean, isWaitingRoom?: boolean) => {
   if (toPattern(cardPattern) === PatternType.P2020 && hasGeometry && (objType === ObjectType.KRO || objType === ObjectType.KRTZ)) {
       var canEditKR = false;

       // Check if edit can be made on downloaded records
       if (isWaitingRoom && objectStatus === ObjectState.DownloadedForEdition) return canEditKR;

       // Check object status permissions
       const editable = [ObjectState.Assigned, ObjectState.WaitingRoomEdition, ObjectState.DownloadedForEdition];
       canEditKR = editable.includes(objectStatus as ObjectState);

       // Check extended user permissions
       if (canEditKR) {
           if (extendedUserPermissions.userObjectCategories?.length! > 0) {
               if (objType === ObjectType.KRO) canEditKR = extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.Landslide)!;
               if (objType === ObjectType.KRTZ) canEditKR = extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.EndangeredArea)!;
               if (!canEditKR) return false;
           }
           if (teryts && (extendedUserPermissions.userGminy?.length! > 0 || extendedUserPermissions.userPowiaty?.length! > 0)) {
               var terytListKR = teryts.split(', ');
               canEditKR = terytListKR.some(item => 
                   extendedUserPermissions.userGminy?.includes(item) || extendedUserPermissions.userPowiaty?.includes(item.substring(0, 4)));
           }
       }
       
       return canEditKR;
   }   
}

export const onlineOfflinePermissionsCheck = (newUseCaseDefinitions: UseCaseDefinitions[]) => {
    var onlinePerms = newUseCaseDefinitions.find(item => item.useCase.koD_PU === "PU.SOPO-P.001");
    var offlinePerms = newUseCaseDefinitions.find(item => item.useCase.koD_PU === "PU.SOPO-P.005");
    var onlineGroups = onlinePerms?.portalGroups && onlinePerms?.portalGroups.length > 0 ? onlinePerms.portalGroups.map(item => item.nazwa) : [];
    var offlineGroups = offlinePerms?.portalGroups && offlinePerms?.portalGroups.length > 0 ? offlinePerms.portalGroups.map(item => item.nazwa) : [];
    return JSON.stringify(onlineGroups.sort((a, b) => a.localeCompare(b))) === JSON.stringify(offlineGroups.sort((a, b) => a.localeCompare(b)));
}

export const getCorrectFormPermissions = (isProtocol: boolean, objectType: string, teryts: string, functionsPermissions: FunctionTypes, extendedUserPermissions: ExtendedUserPermissions) => {
    if ((isProtocol && !functionsPermissions.correctPO.active) || (!isProtocol && !functionsPermissions.processK.active)) return false;

    var isAllowed = false;

    if (extendedUserPermissions.userObjectCategories?.length! > 0) {
        if (isProtocol) {
            isAllowed = extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.Protocol)! &&
            objectType === ObjectType.POO ? extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.Landslide)! : 
            extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.EndangeredArea)!;
        }
        else {
            isAllowed = objectType === ObjectType.KRO ? extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.Landslide)! : 
            extendedUserPermissions.userObjectCategories?.includes(ObjectCategories.EndangeredArea)!;
        } 
        if (!isAllowed) return false;          
    }
    if (teryts && teryts !== null && (extendedUserPermissions.userGminy?.length! > 0 || extendedUserPermissions.userPowiaty?.length! > 0)) {
        var terytList = teryts.split(', ');
        isAllowed = terytList.some(item => 
            extendedUserPermissions.userGminy?.includes(item) || extendedUserPermissions.userPowiaty?.includes(item.substring(0, 4)));
    }
    else isAllowed = true;

    return isAllowed
}